import type { OrgKeyName } from '@/types';

export type ApiKeyCategory = 'llm' | 'data_source' | 'search' | 'tearsheet' | 'parallel';

export interface KeyMeta {
  name: OrgKeyName;
  label: string;
  required?: boolean;
  apolloInfo?: boolean;
}

export const KEYS: Record<OrgKeyName, KeyMeta> = {
  openai_api_key: { name: 'openai_api_key', label: 'OpenAI API Key', required: true },
  anthropic_api_key: { name: 'anthropic_api_key', label: 'Claude (Anthropic) API Key' },
  gemini_api_key: { name: 'gemini_api_key', label: 'Gemini (Google) API Key' },
  perplexity_api_key: { name: 'perplexity_api_key', label: 'Perplexity API Key' },
  apollo_api_key: { name: 'apollo_api_key', label: 'Apollo.io API Key', apolloInfo: true },
  coresignal_api_key: { name: 'coresignal_api_key', label: 'Coresignal API Key' },
  gmaps_api_key: { name: 'gmaps_api_key', label: 'Google Maps Extractor API Key' },
  apify_api_key: { name: 'apify_api_key', label: 'LinkedIn (Apify) API Key' },
  hunter_api_key: { name: 'hunter_api_key', label: 'Hunter API Key (email verification)' },
  serper_api_key: { name: 'serper_api_key', label: 'Serper (Google Search) API Key' },
  news_api_key: { name: 'news_api_key', label: 'News API Key (Brave Search API)' },
  gamma_api_key: { name: 'gamma_api_key', label: 'Gamma API Key (Tearsheet polish)' },
  parallel_api_key: { name: 'parallel_api_key', label: 'Parallel.ai API Key (FindAll)' },
};

export const KEY_GROUPS = [
  {
    category: 'llm',
    title: 'Language Models',
    description: 'Required for scoring, rationale generation, and AI enrichment.',
    keys: ['openai_api_key', 'anthropic_api_key', 'gemini_api_key', 'perplexity_api_key'],
  },
  {
    category: 'data_source',
    title: 'Data Sources',
    description: 'Optional — each enabled source adds more company discovery coverage.',
    keys: [
      'apollo_api_key',
      'coresignal_api_key',
      'gmaps_api_key',
      'apify_api_key',
      'hunter_api_key',
    ],
  },
  {
    category: 'search',
    title: 'Search Providers',
    description: 'Optional — used for broader market web searches.',
    keys: ['serper_api_key', 'news_api_key'],
  },
  {
    category: 'tearsheet',
    title: 'Tearsheet polish',
    description: 'Optional — Gamma generates the polished PDF deck after research.',
    keys: ['gamma_api_key'],
  },
  {
    category: 'parallel',
    title: 'Parallel AI',
    description: 'FindAll company discovery. Added by Quralyst for the organization.',
    keys: ['parallel_api_key'],
  },
] as const satisfies ReadonlyArray<{
  category: ApiKeyCategory;
  title: string;
  description: string;
  keys: readonly OrgKeyName[];
}>;

export const KEY_CATEGORY = Object.fromEntries(
  KEY_GROUPS.flatMap((group) => group.keys.map((key) => [key, group.category])),
) as Record<OrgKeyName, ApiKeyCategory>;

export const ORG_ONLY_KEYS = new Set<OrgKeyName>(
  KEY_GROUPS.filter((group) => group.category !== 'data_source').flatMap((group) => group.keys),
);

export const USER_EDITABLE_KEYS = new Set<OrgKeyName>(
  KEY_GROUPS.find((group) => group.category === 'data_source')?.keys ?? [],
);
